import { FC } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { SVG_MUI } from "@ryde/assets";
import {
  BulkActionsPanel,
  IBulkActionItem,
  useGenericExportBulkAction,
} from "@ryde/controls";
import { usePageContext } from "@ryde/redux";
import {
  ActionsEnum,
  bulkOperationsDrawerAF,
  DrawerType,
  systemSettingsDtoAtom,
  useDrawers,
} from "@ryde/shared";

import {
  BaseDrawer,
  DrawerContentStyled,
  DrawerHeader,
} from "../../../drawers";

export const BulkOperationsPriceRateDrawer: FC = () => {
  const { entity } = usePageContext();
  const { t } = useTranslation();
  const { openDrawer } = useDrawers();
  const systemSettingsDto = useRecoilValue(systemSettingsDtoAtom);
  const enableSupplierPriceRates =
    systemSettingsDto?.metadata?.enableSupplierPriceRates;
  const genericExportAction = useGenericExportBulkAction();

  const setBulkOperationsDrawerOpened = useSetRecoilState(
    bulkOperationsDrawerAF(entity),
  );

  const onCloseDrawer = useEvent(() => setBulkOperationsDrawerOpened(false));

  const openUpdateBasePriceDrawer = (): void => {
    onCloseDrawer();
    openDrawer({
      drawerProps: {
        type: DrawerType.UpdateBasePrice,
      },
    });
  };

  const openCopyToSupplierPriceRatesDrawer = (): void => {
    onCloseDrawer();
    openDrawer({
      drawerProps: {
        type: DrawerType.CopyToSupplierPriceRates,
      },
    });
  };

  const actions: IBulkActionItem[] = [
    {
      key: "updateBasePrice",
      label: t(`priceRate.updateBasePrice`),
      hint: t(`priceRate.bulk.hints.updateBasePrice`),
      icon: SVG_MUI.Coins2SVG,
      onClick: openUpdateBasePriceDrawer,
    },
    {
      key: "copyToSupplierPriceRates",
      label: t(`priceRate.copyToSupplierPriceRates`),
      hint: t(`priceRate.bulk.hints.copyToSupplierPriceRates`),
      icon: SVG_MUI.Briefcase2SVG,
      isHidden: !enableSupplierPriceRates,
      permission: { entity: "supplierPriceRate", action: ActionsEnum.Update },
      onClick: openCopyToSupplierPriceRatesDrawer,
    },
    genericExportAction,
  ];

  return (
    <BaseDrawer onCloseDrawer={onCloseDrawer}>
      <DrawerHeader
        title={t("general.BULK_ACTIONS")}
        onCloseDrawer={onCloseDrawer}
      />
      <DrawerContentStyled className="bulk-action-drawer">
        <BulkActionsPanel actions={actions} />
      </DrawerContentStyled>
    </BaseDrawer>
  );
};
